import React from 'react';
import Svg, { Circle, Line, Path, Text as SvgText } from 'react-native-svg';
import { getDefaultFontFamily, useThemeColors } from 'smoothie';

import {
    type FreeToSpendBudgetGraphPeriod,
    type FreeToSpendBudgetGraphProps,
    type FreeToSpendDataPoint,
} from '../../types/FreeToSpendTypes';

const PADDING = { top: 0, right: 16, bottom: 0, left: 16 };
const CHART_HEIGHT = 88;
const MIN_Y = 8;
const LABEL_AREA_HEIGHT = 20;

const toDate = (d: Date | string): Date => {
    return d instanceof Date ? d : new Date(d);
};

const scaleY = (
    amount: number,
    maxScale: number,
    graphHeight: number,
): number => {
    const t = amount / (maxScale || 1);
    return PADDING.top + graphHeight - t * graphHeight;
};

const SAME_Y_EPS = 0.5;

const generateSmoothPath = (
    points: Array<{ x: number; y: number }>,
): string => {
    if (points.length === 0) return '';
    const first = points[0];
    if (points.length === 1)
        return first !== undefined ? `M ${first.x} ${first.y}` : '';
    let d = first !== undefined ? `M ${first.x} ${first.y}` : '';
    for (let i = 1; i < points.length; i++) {
        const p = points[i];
        const prev = points[i - 1];
        if (p === undefined || prev === undefined) continue;
        const flat = Math.abs(p.y - prev.y) <= SAME_Y_EPS;
        if (flat) {
            d += ` L ${p.x} ${p.y}`;
        } else {
            const cpx = prev.x + (p.x - prev.x) / 2;
            d += ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
        }
    }
    return d;
};

export const getPeriodRange = (
    period: FreeToSpendBudgetGraphPeriod,
    anchor: Date,
): { start: Date; end: Date } => {
    const now = new Date(anchor);
    const start = new Date(now);
    const end = new Date(now);

    if (period === 'monthly') {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setDate(1);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
    } else {
        const day = start.getDay();
        const diff = start.getDate() - day;
        start.setDate(diff + 1);
        start.setHours(0, 0, 0, 0);
        end.setTime(start.getTime());
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
    }
    return { start, end };
};

const WEEKDAY_ABBR = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const BudgetGraph = ({
    budget,
    spendData,
    period,
    width,
    height,
    lineColor = '$fruitfulGreen',
    lineThickness = 2,
    anchorDate,
    isOverBudget = false,
    isLoading: isLoadingFreeToSpendData = false,
}: FreeToSpendBudgetGraphProps) => {
    const { getColor } = useThemeColors();
    const resolvedLineColor = getColor(lineColor);
    const graphWidth = width - PADDING.left - PADDING.right;
    const chartHeight = CHART_HEIGHT;
    const rightEdge = PADDING.left + graphWidth;

    const range = getPeriodRange(period, anchorDate);
    const startDate = range.start;
    const endDate = range.end;

    const sortByDate = (
        a: FreeToSpendDataPoint,
        b: FreeToSpendDataPoint,
    ): number => toDate(a.date).getTime() - toDate(b.date).getTime();
    const sorted = [...spendData].sort(sortByDate);

    const isBeforePeriod = (p: FreeToSpendDataPoint): boolean =>
        toDate(p.date) < startDate;
    const beforePeriod = sorted.filter(isBeforePeriod);
    const lastBeforePeriod = beforePeriod[beforePeriod.length - 1];
    const amountAtPeriodStart =
        lastBeforePeriod !== undefined ? lastBeforePeriod.amount : 0;

    const isInPeriod = (p: FreeToSpendDataPoint): boolean =>
        toDate(p.date) >= startDate && toDate(p.date) <= endDate;
    const periodPoints = sorted.filter(isInPeriod);
    const spendInPeriod = (p: FreeToSpendDataPoint): number =>
        p.amount - amountAtPeriodStart;
    const maxSpendInPeriod =
        periodPoints.length > 0
            ? Math.max(...periodPoints.map(spendInPeriod))
            : 0;
    const baseYScale = Math.max(budget, maxSpendInPeriod);
    // add headroom so "over budget" is visually apparent
    const yScale = isOverBudget ? baseYScale * 1.15 : baseYScale;

    const gridLineX: number[] = [];
    const labelX: number[] = [];
    const dayLabels: string[] = [];
    let numDaysInPeriod = 0;

    const withinNumDays = (d: number): boolean => d <= numDaysInPeriod;
    if (period === 'monthly') {
        numDaysInPeriod = endDate.getDate();
        const monthlyLineDays = [7, 14, 21, 28].filter(withinNumDays);
        for (const day of monthlyLineDays) {
            const t =
                numDaysInPeriod > 1 ? (day - 1) / (numDaysInPeriod - 1) : 0;
            gridLineX.push(PADDING.left + t * graphWidth);
        }
    } else {
        numDaysInPeriod = 7;
        for (let i = 0; i < numDaysInPeriod; i++) {
            const t = numDaysInPeriod > 1 ? i / (numDaysInPeriod - 1) : 0;
            const x = PADDING.left + t * graphWidth;
            gridLineX.push(x);
            labelX.push(x);
            dayLabels.push(WEEKDAY_ABBR[i] ?? 'MO');
        }
    }

    const budgetAtDayOne = budget * (1 / numDaysInPeriod);
    const x0 = PADDING.left;
    const y0_bottom = PADDING.top + chartHeight;
    const y0_budgetStart = scaleY(budgetAtDayOne, yScale, chartHeight);
    const x1 = rightEdge;
    const y1 = scaleY(budget, yScale, chartHeight);
    const budgetAreaPath = `M ${x0} ${isLoadingFreeToSpendData ? chartHeight : y0_budgetStart} L ${x1} ${isLoadingFreeToSpendData ? 0 : y1} L ${x1} ${y0_bottom} L ${x0} ${y0_bottom} Z`;

    const getDayIndex = (date: Date): number => {
        if (period === 'monthly') {
            return Math.min(
                numDaysInPeriod - 1,
                Math.max(0, date.getDate() - 1),
            );
        }
        const daysFromStart = Math.round(
            (date.getTime() - startDate.getTime()) / ONE_DAY_MS,
        );
        return Math.min(numDaysInPeriod - 1, Math.max(0, daysFromStart));
    };

    const toPoint = (p: FreeToSpendDataPoint): { x: number; y: number } => {
        const date = toDate(p.date);
        const dayIndex = getDayIndex(date);
        const x =
            period === 'monthly'
                ? PADDING.left +
                  (numDaysInPeriod > 1
                      ? (dayIndex / (numDaysInPeriod - 1)) * graphWidth
                      : 0)
                : (gridLineX[dayIndex] ?? PADDING.left);
        const y = scaleY(
            Math.max(0, p.amount - amountAtPeriodStart),
            yScale,
            chartHeight,
        );
        return { x, y: Math.max(MIN_Y, y) };
    };
    const points = periodPoints.map(toPoint);

    const spendPath = points.length >= 2 ? generateSmoothPath(points) : '';

    const strokeGrid = 'rgba(0,0,0,0.05)';

    return (
        <Svg width={width} height={height}>
            <Path d={budgetAreaPath} fill="#E8F5EB" />
            {!isLoadingFreeToSpendData && isOverBudget && (
                <Line
                    x1={PADDING.left}
                    y1={y1}
                    x2={rightEdge}
                    y2={y1}
                    stroke="rgba(0,0,0,0.2)"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                />
            )}
            {!isLoadingFreeToSpendData &&
                gridLineX.map((x, i) => (
                    <Line
                        key={i}
                        x1={x}
                        y1={PADDING.top}
                        x2={x}
                        y2={PADDING.top + chartHeight}
                        stroke={strokeGrid}
                        strokeWidth={1}
                    />
                ))}
            {!isLoadingFreeToSpendData && spendPath !== '' && (
                <Path
                    d={spendPath}
                    fill="none"
                    stroke={resolvedLineColor}
                    strokeWidth={lineThickness}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            )}
            {!isLoadingFreeToSpendData &&
                points.length > 0 &&
                (() => {
                    const last = points[points.length - 1];
                    return (
                        <Circle
                            cx={last?.x}
                            cy={last?.y}
                            r={6}
                            fill="white"
                            stroke={resolvedLineColor}
                            strokeWidth={2}
                        />
                    );
                })()}
            {period === 'weekly' &&
                dayLabels.map((label, i) => (
                    <SvgText
                        key={i}
                        x={labelX[i]}
                        y={
                            PADDING.top +
                            chartHeight +
                            LABEL_AREA_HEIGHT / 2 +
                            2
                        }
                        textAnchor="middle"
                        fill="#b7b7b7"
                        fontSize={10}
                        fontFamily={getDefaultFontFamily()}
                    >
                        {label}
                    </SvgText>
                ))}
        </Svg>
    );
};
